import { SMART_CONTRACT_TYPES } from "@/components/SmartContractDesigner/smartContractPluginIDs";
import { FabricDiagramValidationConfig } from "./config/FabricDiagramValidationConfig";
import { DiagramValidationErrorModel } from "./config/DiagramValidationErrorModel";
import { DIAGRAM_ERROR_TYPES } from "./config/DiagramErrorTypes";

/**
 * @description
 */
export class DiagramValidator {
    config;

    extractedOverallSettings = {};

    pluginCounts = {};

    parsedPlugins = [];

    parsedConnectors = [];

    diagramConfigData;

    constructor(config, diagramConfigData) {
        this.config = new FabricDiagramValidationConfig(config.plugins, config.connectors, config.overall);
        this.diagramConfigData = diagramConfigData;
    }

    resetState() {
        this.extractedOverallSettings = {};
        this.pluginCounts = {};
        this.parsedPlugins = [];
        this.parsedConnectors = [];
    }

    validate(diagram) {
        if (typeof diagram === "string") {
            diagram = JSON.parse(diagram);
        }
        this.resetState();
        if (!this.config) {
            return { type: DIAGRAM_ERROR_TYPES.NO_CONFIG_SET, reason: "No config set!" };
        }
        const allValidations = this.getPluginValidations();
        Object.keys(allValidations).forEach(key => {
            if (allValidations[key].config.mandatory) {
                this.extractOverallSettings({ id: key }, allValidations);
            }
        });
        const errors = this.validateIndividualElements(diagram.content, this.getPluginValidations(), diagram);

        const overall = this.runOverallValidations(diagram, this.getOverallValidations());
        const total = [...errors, ...overall];
        return total.length > 0 ? { valid: false, errors: total } : { valid: true, errors: null };
    }

    runOverallValidations(diagram, validations) {
        const errors = [];
        validations.forEach(validation => {
            const customValidatorErrors = validation.call(this, {
                parsedConnectors: this.parsedConnectors,
                parsedPlugins: this.parsedPlugins,
                pluginCounts: this.pluginCounts,
                diagramData: diagram,
                diagramConfigData: this.diagramConfigData
            });
            if (customValidatorErrors && Array.isArray(customValidatorErrors)) {
                errors.push(...customValidatorErrors);
            }
        });
        const pluginOverallErrors = Object.keys(this.extractedOverallSettings).flatMap(pluginID => {
            // checking mandatory plugins status and count
            const errorsForCurrentPlugin = [];
            if (this.pluginCounts[pluginID] === undefined) this.pluginCounts[pluginID] = 0;
            if (this.extractedOverallSettings[pluginID].mandatory) {
                if (this.pluginCounts[pluginID] < this.extractedOverallSettings[pluginID].mandatoryCount) {
                    errorsForCurrentPlugin.push(
                        new DiagramValidationErrorModel({
                            type: DIAGRAM_ERROR_TYPES.INVALID_MANDATORY_COUNT,
                            reason: `Insuficient mandatory plugin instances for plugin type: ${pluginID}`,
                            aditional: { currentValue: this.pluginCounts[pluginID], required: this.extractedOverallSettings[pluginID].mandatoryCount, id: pluginID }
                        })
                    );
                }
            }
            // checking maxInstances
            if (this.pluginCounts[pluginID] > this.extractedOverallSettings[pluginID].maxInstances) {
                errorsForCurrentPlugin.push(
                    new DiagramValidationErrorModel({
                        type: DIAGRAM_ERROR_TYPES.INVALID_MAXIMUM_PLUGIN_INSTANCES,
                        reason: `Invalid max instances for plugin: ${pluginID}`,
                        aditional: { currentValue: this.pluginCounts[pluginID], required: this.extractedOverallSettings[pluginID].maxInstances, id: pluginID }
                    })
                );
            }
            // checking minInstances
            if (this.pluginCounts[pluginID] < this.extractedOverallSettings[pluginID].minInstances) {
                errorsForCurrentPlugin.push(
                    new DiagramValidationErrorModel({
                        type: DIAGRAM_ERROR_TYPES.INVALID_MINIMUM_PLUGIN_INSTANCES,
                        reason: `Invalid min instances for plugin: ${pluginID}`,
                        aditional: { currentValue: this.pluginCounts[pluginID], required: this.extractedOverallSettings[pluginID].minInstances, id: pluginID }
                    })
                );
            }
            return errorsForCurrentPlugin;
        }, 1);
        errors.push(...pluginOverallErrors);
        return errors;
    }

    validateSinglePlugin(plugin, validations, diagram) {
        const validation = validations[plugin.id];
        const errors = [];
        if (validation.config.custom && validation.config.custom.length > 0) {
            validation.config.custom.forEach(customValidation => {
                const customValidatorErrors = customValidation.call(this, {
                    parsedConnectors: this.parsedConnectors,
                    parsedPlugins: this.parsedPlugins,
                    pluginCounts: this.pluginCounts,
                    diagramData: diagram,
                    diagramConfigData: this.diagramConfigData,
                    currentPlugin: plugin,
                    currentPluginValidation: validation
                });
                if (customValidatorErrors && Array.isArray(customValidatorErrors)) {
                    errors.push(...customValidatorErrors);
                } else if (customValidatorErrors && typeof customValidatorErrors === "object" && customValidatorErrors !== null) {
                    errors.push(customValidatorErrors);
                }
            });
        }

        if (validation.config.minIncoming > plugin.incoming.length) {
            errors.push(new DiagramValidationErrorModel({
                type: DIAGRAM_ERROR_TYPES.INVALID_MINIMUM_PLUGIN_INCOMING,
                reason: `Insuficient incoming connections to plugin with ID: ${plugin.id} and UUID: ${plugin.uuid}`,
                aditional: {
                    currentValue: plugin.incoming.length, required: validation.config.minIncoming, id: plugin.id, uuid: plugin.uuid
                }
            }));
        }
        if (validation.config.minOutgoing > plugin.outgoing.length) {
            errors.push(new DiagramValidationErrorModel({
                type: DIAGRAM_ERROR_TYPES.INVALID_MINIMUM_PLUGIN_OUTGOING,
                reason: `Insuficient outgoing connections from plugin with ID: ${plugin.id} and UUID: ${plugin.uuid}`,
                aditional: {
                    currentValue: plugin.outgoing.length, required: validation.config.minOutgoing, id: plugin.id, uuid: plugin.uuid
                }
            }));
        }
        if (validation.config.maxIncoming < plugin.incoming.length) {
            errors.push(new DiagramValidationErrorModel({
                type: DIAGRAM_ERROR_TYPES.INVALID_MAXIMUM_PLUGIN_INCOMING,
                reason: `Too many incoming connections to plugin with ID: ${plugin.id} and UUID: ${plugin.uuid}`,
                aditional: {
                    currentValue: plugin.incoming.length, required: validation.config.maxIncoming, id: plugin.id, uuid: plugin.uuid
                }
            }));
        }
        if (validation.config.maxOutgoing < plugin.outgoing.length) {
            errors.push(new DiagramValidationErrorModel({
                type: DIAGRAM_ERROR_TYPES.INVALID_MAXIMUM_PLUGIN_OUTGOING,
                reason: `Too many outgoing connections from plugin with ID: ${plugin.id} and UUID: ${plugin.uuid}`,
                aditional: {
                    currentValue: plugin.outgoing.length, required: validation.config.maxOutgoing, id: plugin.id, uuid: plugin.uuid
                }
            }));
        }
        return errors;
    }

    extractOverallSettings(plugin, validations) {
        const pluginValidations = validations[plugin.id];
        if (!pluginValidations) return;
        const { config } = pluginValidations;
        if (config.mandatory && !this.extractedOverallSettings[plugin.id]) {
            this.extractedOverallSettings[plugin.id] = {
                mandatory: config.mandatory,
                mandatoryCount: config.mandatoryCount,
                minInstances: config.minInstances,
                maxInstances: config.maxInstances
            };
        }
    }

    saveParsedHistory(plugin) {
        if (this.parsedPlugins.indexOf(plugin.uuid) === -1) {
            if (!this.pluginCounts[plugin.id]) {
                this.pluginCounts[plugin.id] = 1;
            } else {
                this.pluginCounts[plugin.id] += 1;
            }
        }
        if (this.parsedPlugins.indexOf(plugin.uuid) === -1) {
            this.parsedPlugins.push(plugin.uuid);
        }
        if (this.parsedPlugins.indexOf(plugin.uuid) === -1) {
            this.parsedPlugins.push(plugin.uuid);
        }
        plugin.incoming.forEach(incomingConnector => {
            if (this.parsedConnectors.indexOf(incomingConnector) === -1) {
                this.parsedConnectors.push(incomingConnector);
            }
        });
    }

    validateSingleConnector(connectorUuid, validations, diagram) {
        const connector = diagram.connectors.find(connector => connector.uuid === connectorUuid);
        const from = diagram.content.find(plugin => plugin.uuid === connector.from);
        const to = diagram.content.find(plugin => plugin.uuid === connector.to);
        if (!validations[from.id]) return [];
        return validations[from.id].map(forbiddenType => {
            const foundForbidden = to.id === forbiddenType;
            if (foundForbidden) {
                return new DiagramValidationErrorModel({
                    type: DIAGRAM_ERROR_TYPES.FORBIDDEN_CONNECTOR,
                    reason: `Forbidden connector from plugin type ${from.id} to ${forbiddenType}`,
                    aditional: { fromUuid: from.uuid, toUuid: foundForbidden.uuid, connectorUuid }
                });
            }
            return null;
        }).filter(error => error);
    }

    validateIndividualElements(plugins, validations, diagram) {
        const errors = [];
        const startPlugin = plugins.find(plugin => plugin.id === SMART_CONTRACT_TYPES.GENERAL_START_PLUGIN);
        if (!startPlugin) {
            errors.push(new DiagramValidationErrorModel({
                type: DIAGRAM_ERROR_TYPES.NO_GENERAL_START,
                reason: "No General Start Plugin present",
                aditional: {}
            }));
            return errors;
        }
        const self = this;
        // TODO: Add custom validator pe pluginValidationModel pentru loops sa aiba body
        function recursivelyParseFromStart(atPlugin) {
            // Get current PLugin validation errors if any
            const currentPluginError = self.validateSinglePlugin(atPlugin, validations, diagram);
            if (currentPluginError && currentPluginError.length > 0) {
                errors.push(...currentPluginError);
            }
            // validate current plugin outgoing connectors
            const currentConnectorsError = atPlugin.outgoing.flatMap(connector => self.validateSingleConnector(connector, self.getConnectorsValidations(), diagram), 1);
            errors.push(...currentConnectorsError);
            if (currentPluginError && currentPluginError.length > 0) {
                errors.push(...currentPluginError);
            }
            // Saved parsed plugin data
            self.saveParsedHistory(atPlugin);
            // Extract validations that need to be checked at the overall stage
            self.extractOverallSettings(atPlugin, validations);
            for (let i = 0; i < atPlugin.outgoing.length; i++) {
                const connector = diagram.connectors.find(connector => connector.uuid === atPlugin.outgoing[i]);
                const to = diagram.content.find(plugin => plugin.uuid === connector.to);
                recursivelyParseFromStart(to);
            }
        }
        recursivelyParseFromStart(startPlugin);
        return errors;
    }

    getConnectorsValidations() {
        return this.config.getConnectorsValidations();
    }

    getPluginValidations() {
        return this.config.getContentValidations();
    }

    getOverallValidations() {
        return this.config.getOverallValidations();
    }

    notNullOrUndefined(val) {
        return val !== null && val !== undefined;
    }
}
